import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';
import { User } from '@app/models/user.model';
import { measurement } from '@lib/measurement';
import { Unit } from '@lib/measurement/units';
import { updatedTypeMeta } from '@app/dashboard-measurement/types';
import { UserService } from '@app/app-state/user.service';
import { ChartDataInterface } from '@app/models/chart-data.model';

HighchartsMore(Highcharts);
SolidGauge(Highcharts);

@Injectable({ providedIn: 'root' })
export class ExecutiveChartDataService {
    private userUnitMeasurement:string;
    constructor(private user: User,private userService: UserService) { this.userService.userUnitMeasurement$.subscribe(
			measurementUnit => this.userUnitMeasurement = measurementUnit
		)}

    generateChart(data, config): Highcharts.Options {
        if (config.isCombineType && config.dataFieldTwo) {
            return this.generateCombinedChart(data, config);
        }

        if (config.isProjectCostColumn) {
            return this.generateCostColumnChart(data, config);
        }

        if (config.isSingleMetricColumn) {
            return this.generateSingleMetricColumnChart(data, config);
        }

        if (config.isSizeRange) {
            const filtered = data.filter(d => Object.keys(d.fragmentationSizeRanges || {}).length > 0);
            const datasetName = filtered.map(d => d.datasetName);
            const series = this.generateSizeRangeSeriesData(filtered);
            return this.buildHighchartsOptions(config, datasetName, series, '', 'normal', true);
        }

        switch (config.chartType) {
            case 'pie':
                return this.generatePieChart(data, config);

            case 'bar':
            case 'column':
                return this.buildBarOrColumnChart(data, config);

            case 'gauge':
                return this.handleGaugeChart(data, config);

            default:
                console.warn('Unsupported chart type:', config.chartType);
                return {};
        }
    }

    private handleGaugeChart(data, config): Highcharts.Options {
        return this.generateGaugeChart(data, config);
    }


    private getFilteredValues(data, key: string): number[] {
        return data.map(item => parseFloat(item?.[key])).filter(val => !isNaN(val) && val !== 0);
    }

    private getSum(values: number[]): number {
        return values.reduce((acc, val) => acc + val, 0);
    }

    private getAverage(values: number[]): number {
        return values.length ? this.getSum(values) / values.length : 0;
    }

    private formatCurrency(value: number): string {
        return value >= 1_000_000 ? `$${(value / 1_000_000).toFixed(2)} M` :
            value >= 1_000 ? `$${(value / 1_000).toFixed(2)} K` :
                `$${value.toFixed(2)}`;
    }



    private buildHighchartsOptions(config, categories, series, unitAbbreviation, stacking = undefined, shared = false, subtitle = ''): Highcharts.Options {
        return {
            chart: { type: config.chartType },
            title: { text: config.chartTitle },
            subtitle: { text: subtitle || undefined },
            xAxis: { categories, title: { text: config.xAxisLabel } },
            yAxis: {
                min: 0,
                max: config.isSizeRange ? 120 : undefined,
                title: { text: unitAbbreviation ? config.yAxisLabel + ` (${unitAbbreviation})` : config.yAxisLabel }
            },
            tooltip: {
                shared,
                pointFormat: `<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:.2f}</b><br/>`
            },
            plotOptions: { column: { stacking } },
            series
        };
    }

    private buildBarOrColumnChart(data, config): Highcharts.Options {
        const { toUnit, unitAbbreviation } = this.getUnitInfo(config);
        const categories = data.map(item => item.datasetName);
        const values = this.convertAndRound(data, config.dataField, toUnit);
        const total = values.reduce((sum, val) => sum + val, 0);
        const subtitle = `Total ${config.chartTitle}: ${parseFloat(total.toFixed(2))} ${unitAbbreviation}`;
        let series = [{ name: config.yAxisLabel, data: values, type: config.chartType }];
        if (this.isSeriesDataAllZero(series)) {
			series = this.getNoDataSeries(config.chartType);
			categories.splice(0, categories.length, 'No Data');
		}

        return this.buildHighchartsOptions(config, categories, series, unitAbbreviation, undefined, false, subtitle);
    }

    private generateSingleMetricColumnChart(data, config): Highcharts.Options {
        if (!Array.isArray(data)) {
           data = this.combinedDrillShiftSummaryData(data);
        }
        const categories = data.map(d => d.datasetName || d.name || 'Project');
        const values = config.isAverage ? data.map(d => d.totalDailyFootage/d.totalManHours) : data.map(d => parseFloat(d?.[config.dataField]) || 0);
        const total = values.reduce((sum, val) => sum + val, 0);
        const formatted = (total.toFixed(2));
        let series = [{ name: config.chartTitle, type: 'column', data: values }]
        if (this.isSeriesDataAllZero(series)) {
			series = this.getNoDataSeries(config.chartType);
			categories.splice(0, categories.length, 'No Data');
		}

        return {
            chart: { type: 'column' },
            title: { text: config.chartTitle },
            subtitle: { text: `${config.chartTitle}: ${formatted}`, align: 'right', verticalAlign: 'top', useHTML: true },
            xAxis: { categories, title: { text: config.xAxisLabel } },
            yAxis: { min: 0, title: { text: config.yAxisLabel } },
            tooltip: { shared: false, pointFormat: `<span style="color:{series.color}">●</span> <b>{point.y:,.2f}</b><br/>` },
            series: series as Highcharts.SeriesOptionsType[]
        };
    }

    public combinedDrillShiftSummaryData(data) {
        const excludeKeys = ['id', 'domainId', 'siteId', 'subdomainId', 'datasetId', 'datasetName', 'createdAt'];
        return Object.entries(data).map(([datasetName, records]) => {
            let summedResult = {};
            (records as ChartDataInterface[]).forEach(record => {
                Object.entries(record).forEach(([key, value]) => {
                    if (excludeKeys.includes(key)) {
                        if (!summedResult[key]) summedResult[key] = value;
                    } else {
                        const numValue = parseFloat(value);
                        if (typeof value !== 'boolean' && !isNaN(numValue) && isFinite(numValue) && !excludeKeys.includes(key)) {
                            summedResult[key] = (summedResult[key] || 0) + numValue;
                        }
                    }
                });
            });
            return summedResult as ChartDataInterface;
        });
    }

    private calculatePercentageDiff(data, actualKey: string, goalKey?: string): number {
        const actual = this.getSum(this.getFilteredValues(data, actualKey));
        const goal = goalKey ? this.getSum(this.getFilteredValues(data, goalKey)) : null;

        let percent = 0;

        if (goalKey && goal !== null && goal !== 0) {
            percent = ((actual - goal) / actual) * 100;
        } else {
            percent = actual / 100;
        }

        return Math.max(0, +percent.toFixed(2));
    }

    private generateGaugeChart(data, config): Highcharts.Options {
        let value = 0;
        const defaultColors = Highcharts.getOptions().colors;

        if (config.isFinesPercent) value = this.calculatePercentageDiff(data, 'finesPercent', 'finesGoal');
        if (config.isDrillingCost) value = this.calculatePercentageDiff(data, 'drillingCostPerTon', 'drillingCostPerTonGoal');
        if (config.isBlastingCost) value = this.calculatePercentageDiff(data, 'totalBlastingCost', 'totalBlastingCostGoal');
        if (config.isPowerConsumption) value = this.calculatePercentageDiff(data, 'powerConsumptionCost', 'powerConsumptionCostGoal');
        if (config.isExplosiveCost) value = this.calculatePercentageDiff(data, 'explosivesCostPerTon', 'explosivesCostPerTonGoal');
        if (config.isTonnagePercent) value = this.calculatePercentageDiff(data, 'muckpileTonnage', 'muckpileTonnageGoal');
        if (config.isBlastFragCost) value = this.calculatePercentageDiff(data, 'blast_meeting_frag', 'blast_meeting_frag_goal');
        if (config.isBeltCost) value = this.calculatePercentageDiff(data, 'belt_maintainence_cost', 'belt_maintainence_cost_goal');
        if (config.isTruckCost) value = this.calculatePercentageDiff(data, 'truck_maintainence_cost', 'truck_maintainence_cost_goal');
        if (config.isUtilizationPercent) value = this.calculatePercentageDiff(data, 'utilization', 'utilizationGoal');
        if (config.isOverSizeRange) {
            const filtered = data.filter(d => Object.keys(d.fragmentationSizeRanges || {}).length > 0);

            const transformedData = filtered.map(d => ({
                oversizeActual: Number(
                    measurement.toMaxDecimals(d.fragmentationSizeRanges?.rocksAreaPercent?.[d.fragmentationSizeRanges.rocksAreaPercent.length - 1], 2)
                ),
                oversizeGoal: Number(d.oversizeGoal || 0)

            }));
            value = this.calculatePercentageDiff(transformedData, 'oversizeActual', 'oversizeGoal');
        }

        const dynamicMax = Math.ceil((value || 100) / 50) * 50;
        return {
            chart: { type: 'gauge', backgroundColor: null },
            title: { text: config.chartTitle },
            pane: {
                startAngle: -150,
                endAngle: 150,
                background: [{ backgroundColor: '#EEE', borderWidth: 0, outerRadius: '100%', innerRadius: '60%' }]
            },
            yAxis: {
                min: 0,
                max: dynamicMax,
                tickInterval: dynamicMax / 5,
                title: { text: config.yAxisLabel },
                plotBands: [
                    { from: 0, to: dynamicMax * 0.5, color: defaultColors[0] },
                    { from: dynamicMax * 0.5, to: dynamicMax * 0.8, color: defaultColors[1] },
                    { from: dynamicMax * 0.8, to: dynamicMax, color: defaultColors[2] }
                ]
            },
            plotOptions: {
                gauge: {
                    dataLabels: {
                        format: '<span style="font-size:1.5em">{y:.1f}%</span>',
                        borderWidth: 0
                    },
                    dial: { radius: '90%' }
                }
            },
            series: [{
                type: 'gauge',
                name: config.yAxisLabel || 'Metric',
                data: [value],
                tooltip: {
                    valueSuffix: '%',
                    valueDecimals: 1
                }
            }]
        };
    }

    private generateCostColumnChart(data, config): Highcharts.Options {
        const costFields = this.getDrillAndBlastCostFields();
        const categories = data.map(d => d.datasetName || 'Project');
        let series = costFields.map(field => ({
            name: field.label,
            type: 'column',
            data: data.map(d => parseFloat(d?.[field.key]) || 0)
        }));

        const total = series.reduce((sum, s) => sum + s.data.reduce((a, b) => a + b, 0), 0);
        const formatted = this.formatCurrency(total);
        if (this.isSeriesDataAllZero(series)) {
			series = this.getNoDataSeries(config.chartType);
			categories.splice(0, categories.length, 'No Data');
		}

        return {
            chart: { type: 'column' },
            title: { text: config.chartTitle },
            subtitle: { text: `Total: ${formatted}`, align: 'right', verticalAlign: 'top', useHTML: true },
            xAxis: { categories, title: { text: config.xAxisLabel } },
            yAxis: { min: 0, title: { text: config.yAxisLabel } },
            tooltip: { shared: true, pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>${point.y:,.0f}</b><br/>' },
            plotOptions: { column: { grouping: true, borderWidth: 0 } },
            series: series as Highcharts.SeriesOptionsType[]
        };
    }

    private getDrillAndBlastCostFields() {
        return [
            { key: 'drillingCostPerTon', label: 'Drilling' },
            { key: 'explosivesCostPerTon', label: 'Explosives' },
            { key: 'totalBlastingCost', label: 'Blasting' },
            { key: 'secondaryBreakageCost', label: 'Sec. Breakage cost' },
            { key: 'powerConsumptionCostGoal', label: 'Crusher power consumption cost' }
        ];
    }

    private aggregateDrillAndBlastCosts(costs) {
        return this.getDrillAndBlastCostFields().map(field => {
            const total = costs.reduce((sum, item) => sum + (parseFloat(item?.[field.key]) || 0), 0);
            return { name: field.label, value: parseFloat(total.toFixed(2)) };
        });
    }

    private generatePieChart(data, config): Highcharts.Options {
        const costData = this.aggregateDrillAndBlastCosts(data);
        const total = costData.reduce((sum, item) => sum + item.value, 0);
        const formatted = this.formatCurrency(total);
        const isAllZero = costData.length === 0 || costData.every(item => item.value === 0);
        const chartData = isAllZero ? this.getNoDataSeries('pie') : costData.map(c => ({name: c.name, y: c.value, type: 'pie' }));

        return {
            chart: { type: 'pie' },
            title: { text: config.chartTitle },
            subtitle: { text: `Total cost: ${formatted}`, align: 'right', verticalAlign: 'top', y: 20, x: -10, useHTML: true },
            plotOptions: { pie: { innerSize: config.isDonut ? '60%' : '0%', dataLabels: { enabled: false }, showInLegend: true } },
            tooltip: { pointFormat: '<b>${point.y:,.0f}</b>' },
            series: [{ name: 'Costs', type: 'pie', data: chartData }]
        };
    }

    private generateSizeRangeSeriesData(response) {
        const results = response.filter(res => res?.fragmentationSizeRanges?.rocksAreaPercent && res?.finesPercent);
        return this.mergeFragmentationData(results, this.user.unit('smallDistance'));
    }

    private mergeFragmentationData(filterResponse, unit) {
        const seriesData = [], nameToIndex = new Map();

        const pushToSeries = (name, datasetIndex, value) => {
            if (!nameToIndex.has(name)) {
                const entry = { type: 'column', name, data: Array(datasetIndex).fill(null) };
                entry.data.push(value);
                seriesData.push(entry);
                nameToIndex.set(name, seriesData.length - 1);
            } else {
                seriesData[nameToIndex.get(name)].data.push(value);
            }
        };

        filterResponse.forEach((dataset, index) => {
            const minDia = measurement.format(dataset.minimumRocksDiameter, { unit });
            pushToSeries(`Fines < ${minDia}`, index, Number(measurement.toMaxDecimals(dataset.finesPercent, 2)));

            const sizeMm = dataset.fragmentationSizeRanges.sizeMm;
            if (!sizeMm.includes(Infinity) || !sizeMm.includes(-Infinity)) {
                dataset.fragmentationSizeRanges.sizeMm = [-Infinity, ...sizeMm, Infinity];
            }

            sizeMm.forEach((size, i) => {
                if (i < sizeMm.length - 1) {
                    const start = measurement.format(size / 1000, { unit });
                    const end = measurement.format(sizeMm[i + 1] / 1000, { unit });
                    const percent = Number(measurement.toMaxDecimals(dataset.fragmentationSizeRanges.rocksAreaPercent[i], 2));
                    pushToSeries(`${start} to ${end}`, index, percent);
                }
            });

            seriesData.forEach(entry => {
                if (entry.data.length < index + 1) entry.data.push(null);
            });
        });

        const sorted = seriesData
            .filter(entry => !entry.name.startsWith("Fines"))
            .sort((a, b) => parseFloat(a.name.split(' to ')[0]) - parseFloat(b.name.split(' to ')[0]));

        const fines = seriesData.find(entry => entry.name.startsWith("Fines"));
        if (fines) sorted.push(fines);

        return sorted;
    }

    private getUnitInfo(config) {
		const metricUnit: Unit = updatedTypeMeta[config.valueUnit].metricUnit;
		const imperialUnit: Unit = updatedTypeMeta[config.valueUnit].imperialUnit;
		const userUnit = this.userService.user.unit(config.valueUnit);
		const toUnit: Unit = this.userUnitMeasurement === 'imperial' ? imperialUnit : metricUnit;
		const unitAbbreviation = measurement.abbreviation(userUnit);
		return { toUnit, unitAbbreviation };
	}

	private convertAndRound(data, field, toUnit) {
		return data.map(item => {
			const value = parseFloat(item?.[field]) || 0;
			const converted = measurement.convert(value, toUnit);
			return Math.round(converted * 100) / 100;
		});
	}

    private isSeriesDataAllZero(series): boolean {
		return series.every(s => s.data.every(val => val === 0));
	}

	private getNoDataSeries(chartType: string = 'column') {
		return [{
			name: 'No Data',
			type: chartType as any,
			data: [{
				y: 1,
				dataLabels: {
					enabled: true,
					format: '0 details'
				}
			}]
		}];
	}

    private generateCombinedChart(data, config): Highcharts.Options {
        const { toUnit, unitAbbreviation } = this.getUnitInfo(config);
        const d50 = this.convertAndRound(data, config.dataField, toUnit);
        const d80 = this.convertAndRound(data, config.dataFieldTwo, toUnit);

        const average = arr => arr.reduce((sum, val) => sum + val, 0) / arr.length || 0;
        const subtitle = `Average D50: ${average(d50).toFixed(2)} ${unitAbbreviation}, Average D80: ${average(d80).toFixed(2)} ${unitAbbreviation}`;

        return {
            chart: { type: config.chartType },
            title: { text: config.chartTitle },
            subtitle: { text: subtitle },
            xAxis: { categories: data.map(item => item.datasetName), title: { text: config.xAxisLabel } },
            yAxis: { min: 0, title: { text: config.yAxisLabel + ` (${unitAbbreviation})` } },
            tooltip: { shared: true, pointFormat: `<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:.2f} ${unitAbbreviation}</b><br/>` },
            series: [
                { name: 'D50', data: d50, type: config.seriesType },
                { name: 'D80', data: d80, type: config.seriesType1 }
            ]
        };
    }
}
